import type { Paragraph, TextRun } from '@/api/types/stowed-element'
import {
  applyUpdateToLineRange,
  mergeAdjacentRuns,
  mergeTextRunStyle,
} from '@/lib/text/text-style-engine'
import type { TextRunStyleUpdate } from '@/lib/text/types/text-run-style-update'
import { buildCharRunMap, paragraphsToPlainText } from '@/lib/shapes/shape-text'

/**
 * Apply a style update to the `[charStart, charEnd)` flat character range, **splitting runs at the
 * range boundaries**. Character offsets follow the same order as `paragraphsToPlainText` (paragraphs
 * joined by `\n`, textLines/textRuns concatenated without separator within each paragraph).
 *
 * This used to restyle any run the range merely *overlapped*, in full. Because typing into a shape
 * coalesces into one run per paragraph (`applyEditToParagraphs` → `mergeAdjacentRuns`), selecting the
 * first two words of a sentence and changing the font restyled the whole paragraph the user had just
 * typed — QA's "the last written paragraph changes font, not just the selection" (WMPCM-9070).
 *
 * The per-line split is delegated to `applyUpdateToLineRange`, the same splitter the text-box range
 * path uses, so both frame kinds share one implementation (and its `mergeAdjacentRuns` coalescing).
 * Note this is NOT `applyStyleToExactCharRange`: that one flattens every paragraph to a single
 * `textLine`, which would destroy the multi-`<TL>` line structure a shape can still transiently
 * carry and shift every downstream offset. (Since `unbakeShapeTextParagraphs` normalizes on load,
 * a paragraph is usually already single-line by the time this runs — but do not lean on that:
 * nothing enforces it between a decode and the next edit.)
 */
export function applyStyleToCharRange(
  paragraphs: Paragraph[],
  update: TextRunStyleUpdate,
  charStart: number,
  charEnd: number
): Paragraph[] {
  if (charStart >= charEnd) return paragraphs

  let offset = 0

  return paragraphs.map((paragraph, pIdx) => {
    const paragraphStart = offset

    const newLines = (paragraph.textLines ?? []).map((line) => {
      const lineStart = offset
      const lineLength = (line.textRuns ?? []).reduce(
        (sum, run) => sum + (run.textContent ?? '').length,
        0
      )

      offset += lineLength

      // Global range clipped to this line, then rebased to line-local offsets — the space
      // `applyUpdateToLineRange` works in.
      const localStart = Math.max(charStart, lineStart) - lineStart
      const localEnd = Math.min(charEnd, lineStart + lineLength) - lineStart

      if (localStart >= localEnd) return line

      return applyUpdateToLineRange(line, localStart, localEnd, update)
    })

    const paragraphEnd = offset
    const paragraphOverlaps = paragraphEnd > charStart && paragraphStart < charEnd

    if (pIdx < paragraphs.length - 1) offset += 1

    return {
      ...paragraph,
      ...(update.alignment !== undefined && paragraphOverlaps
        ? { alignment: update.alignment }
        : {}),
      // Paragraph-level, same as alignment above.
      ...(update.dropCap !== undefined && paragraphOverlaps ? { dropCap: update.dropCap } : {}),
      ...(update.dropCapLines !== undefined && paragraphOverlaps
        ? { dropCapLines: update.dropCapLines }
        : {}),
      ...(update.dropCapOffset !== undefined && paragraphOverlaps
        ? { dropCapOffset: update.dropCapOffset }
        : {}),
      textLines: newLines,
    }
  })
}

/**
 * Kerning value for the glyph pair immediately before a collapsed caret in the flat-text model.
 * Unavailable when the selection isn't collapsed, the caret sits at the very start/end of the
 * flattened text, or is adjacent to a paragraph break — none of those have a real glyph pair.
 */
export function getKerningStateAtFlatCaret(
  paragraphs: Paragraph[],
  caretIndex: number,
  isCollapsed: boolean
): { available: boolean; value: number } {
  const unavailable = { available: false, value: 0 }

  if (!isCollapsed) return unavailable

  const text = paragraphsToPlainText(paragraphs)

  if (caretIndex <= 0 || caretIndex >= text.length) return unavailable
  if (text[caretIndex - 1] === '\n' || text[caretIndex] === '\n') return unavailable

  const run = buildCharRunMap(paragraphs)[caretIndex - 1]

  return { available: true, value: run?.kerning ?? 0 }
}

/**
 * Apply a style update to exactly `[charStart, charEnd)` of the flat text, splitting runs at the
 * boundary rather than restyling any run the range merely overlaps (unlike `applyStyleToCharRange`,
 * which is intentionally coarse for whole-selection styling like Tracking). Needed for Kerning,
 * which must change only the single character immediately before a caret.
 */
export function applyStyleToExactCharRange(
  paragraphs: Paragraph[],
  update: TextRunStyleUpdate,
  charStart: number,
  charEnd: number
): Paragraph[] {
  if (charStart >= charEnd) return paragraphs

  type CharEntry = { char: string; style: Partial<TextRun>; isParaSep: boolean }
  const chars: CharEntry[] = []

  paragraphs.forEach((para, paraIdx) => {
    ;(para.textLines ?? []).forEach((line) => {
      ;(line.textRuns ?? []).forEach((run) => {
        const { textContent, ...style } = run

        for (const char of textContent ?? '') {
          chars.push({ char, style, isParaSep: false })
        }
      })
    })
    if (paraIdx < paragraphs.length - 1) {
      chars.push({ char: '\n', style: {}, isParaSep: true })
    }
  })

  for (let i = Math.max(0, charStart); i < Math.min(charEnd, chars.length); i++) {
    const entry = chars[i]!

    if (entry.isParaSep) continue
    chars[i] = { ...entry, style: mergeTextRunStyle(entry.style as TextRun, update) }
  }

  const groups: Array<Array<{ char: string; style: Partial<TextRun> }>> = [[]]

  for (const c of chars) {
    if (c.isParaSep) {
      groups.push([])
    } else {
      groups[groups.length - 1]!.push({ char: c.char, style: c.style })
    }
  }

  return groups.map((group, paraIdx) => {
    const origPara = paragraphs[paraIdx] ?? paragraphs[paragraphs.length - 1]
    const singleCharRuns: TextRun[] = group.map(({ char, style }) => ({
      ...(style as TextRun),
      textContent: char,
    }))
    const runs: TextRun[] =
      singleCharRuns.length > 0
        ? mergeAdjacentRuns(singleCharRuns)
        : [
            (() => {
              const origRun = origPara?.textLines?.[0]?.textRuns?.[0]

              return origRun ? { ...origRun, textContent: '' } : { textContent: '' }
            })(),
          ]

    return {
      ...origPara,
      textLines: [{ textRuns: runs }],
    }
  })
}
